import z from "zod";
import { addressSchema } from "../address";
import { serviceFormSchema } from "../organization";
import { ZodStringNullable } from "../zod-types/zod-string-nullable";
import { calculationFormPositionItem } from "./calculation-form-position.schema";

export const calculationFormServiceItem = serviceFormSchema
	.omit({
		fulfillmentType: true,
		number: true,
		parentServiceId: true,
	})
	.extend({
		type: z.enum(["service", "block"]).default("service"),
		unit: ZodStringNullable,
		organizationServiceId: z.uuid().nullish(),
		parentServiceCalculationItemId: z.uuid().nullable().optional(),
		ranking: z.number().min(0),
		isEventualPosition: z.boolean().default(false),
		quantity: z.number().nullish().optional(),
		unitPrice: z.number().nullish().optional(),
		positions: z.array(calculationFormPositionItem).optional(),
	});

type DimensionKey = "meters" | "length" | "width" | "height" | "quantity" | "manualMeasurement";

export const calculationFormMetaSchema = z
	.object({
		offerNumber: ZodStringNullable,
		projectName: ZodStringNullable,
		clientId: ZodStringNullable,
	})
	.and(addressSchema);

export const calculationFormSchema = z
	.object({
		name: z.string().min(1, "Name is required"),
		type: z.enum(["parsc", "offer"]),
		services: z.array(calculationFormServiceItem),
	})
	.and(calculationFormMetaSchema)
	.superRefine((data, ctx) => {
		function requiredDimensionKeys(unit: string | null): DimensionKey[] {
			switch (unit) {
				case "meters":
				case "meters-week":
					return ["meters"];
				case "square-meters":
				case "square-meters-week":
					return ["length", "width"];
				case "cubic-meters":
					return ["length", "width", "height"];
				case "piece":
				case "piece-week":
					return ["quantity"];
				default:
					return [];
			}
		}

		function requireNumberAt(
			path: (string | number)[],
			value: unknown,
			key: DimensionKey,
			min = 0,
		) {
			if (typeof value !== "number" || Number.isNaN(value) || value < min) {
				ctx.addIssue({
					code: "custom",
					path: [...path, key],
					message:
						min > 0 ? `${key} must be a number >= ${min}` : `${key} must be a non-negative number`,
				});
			}
		}

		data.services.forEach((service, index) => {
			if (service.type === "block") {
				requireNumberAt(["services", index], service.quantity, "quantity", 1);
				return;
			}

			if (!service.unit) {
				ctx.addIssue({
					code: "custom",
					path: ["services", index, "unit"],
					message: "Unit is required",
				});
				return;
			}

			const positions = service.positions ?? [];

			const keys = requiredDimensionKeys(service.unit);
			const isPieceUnit = service.unit === "piece" || service.unit === "piece-week";
			positions.forEach((position, positionIndex) => {
				// A manual measurement overrides the dimension-based calculation, so it is
				// validated in place of `keys` — but never for piece, where `quantity` already
				// is the measurement.
				if (!isPieceUnit && position.manualMeasurement != null) {
					requireNumberAt(
						["services", index, "positions", positionIndex],
						position.manualMeasurement,
						"manualMeasurement",
					);
					return;
				}
				for (const key of keys) {
					requireNumberAt(["services", index, "positions", positionIndex], position[key], key);
				}
			});
		});
	});

export type CalculationFormSchema = z.infer<typeof calculationFormSchema>;
export type CalculationFormServiceItem = z.infer<typeof calculationFormServiceItem>;
export type CalculationFormMetaSchema = z.infer<typeof calculationFormMetaSchema>;
